import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Popover } from 'primeng/popover';
import { Subject, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { InstructionSearchService } from '../../../services/instruction-search.service';
import {
  InstructionPrintService,
  MANUAL_LANGUAGES,
  defaultManualLanguage
} from '../../../services/instruction-print.service';
import { UtilsService } from '../../../services/utils.service';
import {
  InstructionPageComponent,
  InstructionPageInfo
} from '../instruction-page/instruction-page.component';
import { HelpMenuPanelComponent } from '../help-menu-panel/help-menu-panel.component';
import {
  INSTRUCTION_PAGES,
  findInstructionPage,
  findInstructionSection,
  instructionTitleKey,
  relatedPages
} from '../instruction-catalog';
import { SearchHit } from '../help-search';
import { OutlineEntry } from '../instruction-html';

/** A place the reader can return to with Back. */
interface HistoryEntry {
  slug: string;
  query: string;
  filter: string | null;
}

/** Search results shown before "Show the remaining n results". */
const RESULTS_SHOWN = 10;

const SEARCH_DELAY_MS = 120;

/**
 * Contents of the help drawer: a toolbar with Back, search and the
 * all-instructions menu, and below it either the open instruction page
 * (breadcrumb, content, outline and related pages) or the search results.
 */
@Component({
  selector: 'app-help-browser',
  templateUrl: './help-browser.component.html',
  styleUrls: ['./help-browser.component.scss'],
  imports: [
    TranslatePipe,
    ButtonDirective,
    InputText,
    Popover,
    InstructionPageComponent,
    HelpMenuPanelComponent
  ]
})
export class HelpBrowserComponent implements OnInit {
  /** The page the drawer opens on: the one for the view the user is in. */
  @Input({ required: true }) startSlug!: string;

  @ViewChild('body') bodyRef?: ElementRef<HTMLElement>;
  @ViewChild(InstructionPageComponent) page?: InstructionPageComponent;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  readonly pageCount = INSTRUCTION_PAGES.length;

  slug = '';
  history: HistoryEntry[] = [];

  menuOpen = false;
  menuSection = '';

  outline: OutlineEntry[] = [];
  activeHeading = 0;
  related: string[] = [];

  query = '';
  /** Section the results are narrowed to; null shows all. */
  filter: string | null = null;
  /** Results for the query, or null while the first search is running. */
  hits: SearchHit[] | null = null;
  resultsShown = RESULTS_SHOWN;
  activeResult = 0;

  /** The whole manual is being put together for printing. */
  exporting = false;

  /** The languages the whole manual can be printed in. */
  readonly exportLanguageOptions = MANUAL_LANGUAGES;

  /** Printing the whole manual is for super admins only. */
  get canExport(): boolean {
    return this.printService.canPrintManual();
  }

  private translateService = inject(TranslateService);
  private searchService = inject(InstructionSearchService);
  private printService = inject(InstructionPrintService);
  private utilsService = inject(UtilsService);
  private destroyRef = inject(DestroyRef);
  private query$ = new Subject<string>();

  ngOnInit(): void {
    this.slug = this.startSlug;
    this.related = relatedPages(this.slug, []);
    this.query$
      .pipe(
        debounceTime(SEARCH_DELAY_MS),
        switchMap((query) =>
          query.trim()
            ? this.searchService.search(query, this.lang())
            : of<SearchHit[]>([])
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((hits) => {
        this.hits = hits;
        this.activeResult = Math.min(
          this.activeResult,
          Math.max(this.filteredHits.length - 1, 0)
        );
      });
  }

  get searching(): boolean {
    return !!this.query.trim();
  }

  title(slug: string): string {
    const key = instructionTitleKey(slug);
    const title = this.translateService.instant(key);
    return title === key ? '' : title;
  }

  sectionLabel(sectionId: string | undefined): string {
    const section = sectionId ? findInstructionSection(sectionId) : undefined;
    return section ? this.translateService.instant(section.labelKey) : '';
  }

  get currentSection(): string | undefined {
    return findInstructionPage(this.slug)?.section;
  }

  parentOf(slug: string): string | undefined {
    return findInstructionPage(slug)?.parent;
  }

  sectionOf(slug: string): string | undefined {
    return findInstructionPage(slug)?.section;
  }

  /* ---------- navigation ---------- */

  /** Opens a page, keeping the current page or search for Back. */
  open(slug: string): void {
    this.menuOpen = false;
    if (slug === this.slug && !this.searching) {
      return;
    }
    this.history.push({
      slug: this.slug,
      query: this.query,
      filter: this.filter
    });
    this.show(slug, '', null);
  }

  back(): void {
    const entry = this.history.pop();
    if (entry) {
      this.menuOpen = false;
      this.show(entry.slug, entry.query, entry.filter);
    }
  }

  private show(slug: string, query: string, filter: string | null): void {
    if (slug !== this.slug) {
      this.slug = slug;
      this.outline = [];
      this.activeHeading = 0;
      this.related = relatedPages(slug, []);
    }
    this.query = query;
    this.filter = filter;
    this.resultsShown = RESULTS_SHOWN;
    this.activeResult = 0;
    if (query.trim()) {
      this.hits = null;
      this.query$.next(query);
    }
    this.bodyRef?.nativeElement.scrollTo({ top: 0 });
  }

  onPageInfo(info: InstructionPageInfo): void {
    this.outline = info.outline;
    this.activeHeading = 0;
    this.related = relatedPages(this.slug, info.linkedSlugs);
  }

  goToHeading(position: number): void {
    this.activeHeading = position;
    this.page?.scrollToHeading(this.outline[position].index);
  }

  /* ---------- all-instructions menu ---------- */

  toggleMenu(): void {
    if (this.menuOpen) {
      this.menuOpen = false;
      return;
    }
    this.openMenu(this.currentSection);
  }

  openMenu(sectionId: string | undefined): void {
    this.menuSection = sectionId ?? '';
    this.menuOpen = true;
  }

  /* ---------- export ---------- */

  /** What the export button prints in: the UI language, else English. */
  get defaultExportLanguage(): string {
    return defaultManualLanguage(this.lang());
  }

  /**
   * Prints the whole manual (on paper or as a PDF): in the language picked
   * from the chevron's menu, else in the default language.
   */
  async exportManual(language?: string): Promise<void> {
    if (this.exporting || !this.canExport) {
      return;
    }
    this.menuOpen = false;
    this.exporting = true;
    try {
      await this.printService.printManual(
        language ?? this.defaultExportLanguage
      );
    } catch {
      this.utilsService.showError(
        this.translateService.instant(
          'common.instructions-browser.export-pdf-failed'
        )
      );
    } finally {
      this.exporting = false;
    }
  }

  /* ---------- search ---------- */

  onQueryInput(value: string): void {
    this.menuOpen = false;
    this.query = value;
    this.filter = null;
    this.resultsShown = RESULTS_SHOWN;
    this.activeResult = 0;
    this.query$.next(value);
  }

  clearQuery(): void {
    this.onQueryInput('');
    this.searchInput?.nativeElement.focus();
  }

  prepareSearch(): void {
    this.searchService.prepare(this.lang());
  }

  get filteredHits(): SearchHit[] {
    const hits = this.hits ?? [];
    return this.filter
      ? hits.filter((h) => this.sectionOf(h.slug) === this.filter)
      : hits;
  }

  get visibleHits(): SearchHit[] {
    return this.filteredHits.slice(0, this.resultsShown);
  }

  /** Sections with results, most results first. */
  get sectionFilters(): { id: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const hit of this.hits ?? []) {
      const section = this.sectionOf(hit.slug);
      if (section) {
        counts.set(section, (counts.get(section) ?? 0) + 1);
      }
    }
    return [...counts]
      .map(([id, count]) => ({ id, count }))
      .sort((a, b) => b.count - a.count);
  }

  setFilter(sectionId: string | null): void {
    this.filter = sectionId;
    this.resultsShown = RESULTS_SHOWN;
    this.activeResult = 0;
  }

  showAllResults(): void {
    this.resultsShown = Infinity;
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const count = this.visibleHits.length;
    if (event.key === 'ArrowDown' && count) {
      event.preventDefault();
      this.activeResult = (this.activeResult + 1) % count;
    } else if (event.key === 'ArrowUp' && count) {
      event.preventDefault();
      this.activeResult = (this.activeResult - 1 + count) % count;
    } else if (event.key === 'Enter' && count) {
      event.preventDefault();
      this.open(this.visibleHits[this.activeResult].slug);
    }
  }

  /**
   * Escape closes the menu first, then empties the search, and only then
   * lets the drawer close.
   */
  onEscape(event: Event): void {
    if (this.menuOpen) {
      this.menuOpen = false;
    } else if (this.query) {
      this.clearQuery();
    } else {
      return;
    }
    event.stopPropagation();
  }

  private lang(): string {
    return (
      this.translateService.getCurrentLang() ||
      this.translateService.getFallbackLang() ||
      'fi'
    );
  }
}
